import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import apiClient from '../../api/apiClient';
import AttendanceHeader from '../../components/hris/attendance/AttendanceHeader';
import AttendanceStats from '../../components/hris/attendance/AttendanceStats';
import AttendanceTable from '../../components/hris/attendance/AttendanceTable';
import AttendanceModal from '../../components/hris/attendance/AttendanceModal';
import DeleteAttendanceDialog from '../../components/hris/attendance/DeleteAttendanceDialog';
import OvertimeModal from '../../components/hris/overtime/OvertimeModal';
import { useToast } from '../../context/ToastContext';
import { containerVariants, itemVariants } from '../../utils/animations';

export default function AttendanceList() {
  const { showSuccess, showError } = useToast();
  const [attendances, setAttendances] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [overtimes, setOvertimes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [isAdmin] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAttendance, setEditingAttendance] = useState(null);
  const [deleteAttendanceId, setDeleteAttendanceId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [attendanceForm, setAttendanceForm] = useState({
    employee_id: '',
    date: '',
    clock_in: '',
    clock_out: '',
    status: 'present',
    notes: '',
  });

  // Overtime dialog states
  const [overtimeDialogOpen, setOvertimeDialogOpen] = useState(false);
  const [editingOvertime, setEditingOvertime] = useState(null);
  const [overtimeForm, setOvertimeForm] = useState({
    employee_id: '',
    date: '',
    start_time: '17:00',
    end_time: '20:00',
    duration_hours: '3',
    rate_per_hour: '50000',
    total_amount: '150000',
    description: '',
    status: 'pending',
  });

  const loadData = useCallback(async () => {
    try {
      const response = await apiClient.get(`/hris/attendance?date=${selectedDate}`);
      setAttendances(response.data?.attendances || []);
      setEmployees(response.data?.employees || []);

      // Load overtime data for current month
      try {
        const currentMonth = new Date().getMonth() + 1;
        const currentYear = new Date().getFullYear();
        const overtimeResponse = await apiClient.get(
          `/hris/overtime?month=${currentMonth}&year=${currentYear}`
        );
        setOvertimes(overtimeResponse.overtimes || []);
      } catch (error) {
        setOvertimes([]);
      }
    } catch (error) {
      // Error loading data
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getAttendanceForEmployee = empId => attendances.find(a => a.employee_id === empId);

  const getOvertimeForEmployee = empId => {
    const employeeOvertimes = overtimes.filter(ot => ot.employee_id === empId);
    if (employeeOvertimes.length === 0) return null;

    // Aggregate overtime data for this month
    const totalHours = employeeOvertimes.reduce((sum, ot) => sum + (ot.duration_hours || 0), 0);
    const totalAmount = employeeOvertimes.reduce((sum, ot) => sum + (ot.total_amount || 0), 0);

    // Get the latest status (or most common status)
    const latestOvertime = employeeOvertimes[employeeOvertimes.length - 1];
    const status = latestOvertime?.status || 'pending';

    return {
      total_hours: totalHours,
      total_amount: totalAmount,
      status: status,
      count: employeeOvertimes.length,
    };
  };

  const openDialog = (attendance = null, empId = null) => {
    if (attendance) {
      setEditingAttendance(attendance);
      setAttendanceForm({
        employee_id: attendance.employee_id,
        date: attendance.date,
        clock_in: attendance.clock_in || '',
        clock_out: attendance.clock_out || '',
        status: attendance.status || 'present',
        notes: attendance.notes || '',
      });
    } else {
      setEditingAttendance(null);
      setAttendanceForm({
        employee_id: empId || '',
        date: selectedDate,
        clock_in: '',
        clock_out: '',
        status: 'present',
        notes: '',
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!attendanceForm.employee_id || !attendanceForm.date) return;
    setSaving(true);
    try {
      const dataToSave = {
        ...attendanceForm,
        clock_in: attendanceForm.clock_in || null,
        clock_out: attendanceForm.clock_out || null,
      };
      if (editingAttendance) {
        await apiClient.put(`/hris/attendance/${editingAttendance.id}`, dataToSave);
        showSuccess('Data kehadiran berhasil diperbarui');
      } else {
        await apiClient.post('/hris/attendance', dataToSave);
        showSuccess('Data kehadiran berhasil ditambahkan');
      }
      loadData();
      setDialogOpen(false);
    } catch (error: any) {
      showError(error?.message || 'Gagal menyimpan data kehadiran');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteAttendanceId) return;
    try {
      await apiClient.delete(`/hris/attendance/${deleteAttendanceId}`);
      showSuccess('Data kehadiran berhasil dihapus');
      loadData();
      setDeleteAttendanceId(null);
    } catch (error: any) {
      showError(error?.message || 'Gagal menghapus data kehadiran');
    }
  };

  const openOvertimeDialog = (empId = null, overtime = null) => {
    if (overtime) {
      setEditingOvertime(overtime);
      setOvertimeForm({
        employee_id: overtime.employee_id,
        date: overtime.date,
        start_time: overtime.start_time || '17:00',
        end_time: overtime.end_time || '20:00',
        duration_hours: String(overtime.duration_hours || 3),
        rate_per_hour: String(overtime.rate_per_hour || 50000),
        total_amount: String(overtime.total_amount || 150000),
        description: overtime.description || '',
        status: overtime.status || 'pending',
      });
    } else {
      setEditingOvertime(null);
      setOvertimeForm({
        employee_id: empId || '',
        date: selectedDate,
        start_time: '17:00',
        end_time: '20:00',
        duration_hours: '3',
        rate_per_hour: '50000',
        total_amount: '150000',
        description: '',
        status: 'pending',
      });
    }
    setOvertimeDialogOpen(true);
  };

  const handleSaveOvertime = async () => {
    if (!overtimeForm.employee_id || !overtimeForm.date) {
      showError('Harap isi pegawai dan tanggal');
      return;
    }
    setSaving(true);
    try {
      const dataToSave = {
        ...overtimeForm,
        duration_hours: parseFloat(overtimeForm.duration_hours),
        rate_per_hour: parseFloat(overtimeForm.rate_per_hour),
        total_amount: parseFloat(overtimeForm.total_amount),
      };
      if (editingOvertime) {
        await apiClient.put(`/hris/overtime/${editingOvertime.id}`, dataToSave);
        showSuccess('Data lembur berhasil diperbarui');
      } else {
        await apiClient.post('/hris/overtime', dataToSave);
        showSuccess('Data lembur berhasil ditambahkan');
      }
      loadData();
      setOvertimeDialogOpen(false);
      setEditingOvertime(null);
    } catch (error: any) {
      showError(error?.message || 'Gagal menyimpan data lembur');
    } finally {
      setSaving(false);
    }
  };

  const stats = {
    total: employees.length,
    present: attendances.filter(a => a.status === 'present').length,
    late: attendances.filter(a => a.status === 'late').length,
    absent:
      employees.length - attendances.filter(a => ['present', 'late'].includes(a.status)).length,
    leave: attendances.filter(a => a.status === 'leave').length,
  };

  const filteredEmployees = employees.filter(emp => {
    if (filterStatus === 'all') return true;
    const att = getAttendanceForEmployee(emp.id);
    if (filterStatus === 'present') return att?.status === 'present';
    if (filterStatus === 'late') return att?.status === 'late';
    if (filterStatus === 'absent') return !att || att.status === 'absent';
    if (filterStatus === 'leave') return att?.status === 'leave';
    return true;
  });

  return (
    <motion.div
      className="space-y-4 md:space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div variants={itemVariants}>
        <AttendanceHeader
          selectedDate={selectedDate}
          onDateChange={setSelectedDate}
          isAdmin={isAdmin}
          onAdd={() => openDialog()}
        />
      </motion.div>
      <motion.div variants={itemVariants}>
        <AttendanceStats stats={stats} />
      </motion.div>
      <motion.div variants={itemVariants}>
        <AttendanceTable
          employees={filteredEmployees}
          loading={loading}
          isAdmin={isAdmin}
          filterStatus={filterStatus}
          onFilterChange={setFilterStatus}
          selectedDate={selectedDate}
          getAttendanceForEmployee={getAttendanceForEmployee}
          getOvertimeForEmployee={getOvertimeForEmployee}
          onEdit={att => openDialog(att)}
          onDelete={id => setDeleteAttendanceId(id)}
          onAdd={empId => openDialog(null, empId)}
          onAddOvertime={empId => openOvertimeDialog(empId)}
        />
      </motion.div>
      <AttendanceModal
        open={dialogOpen}
        editing={editingAttendance}
        form={attendanceForm}
        setForm={setAttendanceForm}
        employees={employees}
        saving={saving}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
      />
      <DeleteAttendanceDialog
        show={!!deleteAttendanceId}
        onClose={() => setDeleteAttendanceId(null)}
        onConfirm={handleDelete}
      />
      <OvertimeModal
        open={overtimeDialogOpen}
        editing={editingOvertime}
        form={overtimeForm}
        setForm={setOvertimeForm}
        employees={employees}
        saving={saving}
        onClose={() => setOvertimeDialogOpen(false)}
        onSave={handleSaveOvertime}
      />
    </motion.div>
  );
}
